import { useCallback, useEffect } from 'react';

import { ModalStackReturnType } from 'ui';
import { useModalsStack, useStateHistory } from 'ui/Hooks';

enum FullOrderModalStackModals {
  FullOrder = 'full-order',
  ResendReceipt = 'resend-receipt',
  RefundOrder = 'refund-order',
  NewOrderNote = 'new-order-note',
}

const useFullOrderModalStack = ({ onModalsClose }: { onModalsClose?: () => void } = {}): {
  stack: ModalStackReturnType<FullOrderModalStackModals>;
  open: (modal: FullOrderModalStackModals) => void;
  close: (modal: FullOrderModalStackModals) => void;
  state: ModalStackReturnType<FullOrderModalStackModals>['state'];
} => {
  const stack = useModalsStack([
    FullOrderModalStackModals.FullOrder,
    FullOrderModalStackModals.ResendReceipt,
    FullOrderModalStackModals.RefundOrder,
    FullOrderModalStackModals.NewOrderNote,
  ]);

  const [value, handlers, history] = useStateHistory(stack.state);

  /** Resets history to initial state */
  const handleModalsClosing = useCallback(() => {
    handlers.reset();
    onModalsClose?.();
  }, [handlers, onModalsClose]);

  /**
   * When all modals are closed, reset the history  if the history contains opened modals
   *
   */
  useEffect(() => {
    const hasHistoryOfOpenedModals = history.history.some((modal) =>
      Object.values(modal).some((state) => state === true)
    );

    const isAllModalsClosed = Object.values(value).every((modal) => modal === false);

    if (isAllModalsClosed && hasHistoryOfOpenedModals) {
      handleModalsClosing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync state when the stack changes
  }, [value]);

  /** Syncs the modal state as history  */
  useEffect(() => {
    handlers.set(stack.state);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync state when the stack changes
  }, [stack.state]);

  return {
    stack,
    open: useCallback((modal: FullOrderModalStackModals) => stack.open(modal), [stack]),
    close: useCallback((modal: FullOrderModalStackModals) => stack.close(modal), [stack]),
    state: stack.state,
  };
};

export { FullOrderModalStackModals, useFullOrderModalStack };
